import api from "../api";
import {subscribeToChannel, unsubscribeToChannel} from "../services/pusher";
import store from "../store";
import { timeFrames } from "../services/staticData";
import CancelablePromise from "../services/CancelablePromise";

const state = () => ({
  filters: {
    // Make sure to update resetFilters
    active: {
      users: [],
      campaigns: null,
      exitStrategies: 0,
      temperatures: null,
      dealProbabilityScore: null,
      timeFrames: 0,
      deals: 0,
    },
  },
  records: [],
  pipelineViewId: null,
  reloadingPipelinePromise: null,
  isPageLoading: true,
});

const getters = {
  pageLoading(state) {
    return state.isPageLoading;
  },
  statuses() {
    return store.getters["pipeline/activeStatusesInView"];
  },
  records(state, getters, rootState, rootGetters) {
    return state.records.sort((a, b) => {
      if (!a.nextTask) return 0;

      if (!b.nextTask) return -1;

      return new Date(a.nextTask.due_at) - new Date(b.nextTask.due_at);
    });
  },
  recordsByStatusId: (state, getters) => (statusId) => {
    return getters.records.filter((record) => record.current_status_id === statusId);
  },
  pipelineViewId(state) {
    // trigger to reload active pipeline if the value changes
    return state.pipelineViewId;
  },
  paramFilters(state) {
    const time = timeFrames.find((t) => t.value === state.filters.active.timeFrames);
    if (time) {
      return {
        ...state.filters.active,
        startAt: time.start,
        endAt: time.end,
      };
    }

    return state.filters.active;
  },
};

const mutations = {
  setup(state, data) {
    const map = new Map();

    // insert state first (lower priority)
    for (const item of state.records) {
      map.set(item.id, item);
    }

    // insert data second — overwrites duplicates (higher priority)
    for (const item of data.records) {
      map.set(item.id, item);
    }

    state.records = [...map.values()];
  },
  teardown(state) {
    state.records = [];
    state.reloadingPipelinePromise = null;
    state.isPageLoading = true;
  },
  setOneFilter(state, [filterObject, filter, value]) {
    state.filters[filterObject][filter] = value;
    store.dispatch('updateUserAppFilter').then(r => {});
    store.dispatch("activeState/loadRecords").then(r => {});
  },
  setUserFilters(state, filters) {
    /**
     * to make sure this matches the filters declared on state,
     * this will remove an old filter key
     */
    Object.entries(state.filters).forEach(([key, value]) => {
      if (filters.hasOwnProperty(key)) {
        state.filters[key] = filters[key];
      } else {
        state.filters[key] = value;
      }
    });
  },
  resetFilters(state, filterObject) {
    // Make sure to update state default
    state.filters[filterObject] = {
      users: [],
      campaigns: null,
      exitStrategies: 0,
      temperatures: null,
      dealProbabilityScore: null,
      timeFrames: 0,
      deals: 0,
    };

    store.dispatch("updateUserAppFilter").then((r) => {});
    store.dispatch("activeState/loadRecords").then((r) => {});
  },
  setPipelineViewId(state, viewId) {
    state.pipelineViewId = viewId
  },
  setPageLoading(state, isLoading) {
    state.isPageLoading = isLoading;
  }
};

const actions = {
  setup({commit, state, rootState, getters, dispatch}) {
    subscribeToChannel("activeState", rootState.market.market.id);
    dispatch('loadRecords');
  },
  tearDown({ commit, state, rootState, getters }) {
    unsubscribeToChannel("activeState", rootState.market.market.id);
    commit("teardown");
  },
  async loadRecords({ commit, state, rootState, getters }) {
    if (this.reloadingPipelinePromise) {
      this.reloadingPipelinePromise.cancel();
    }

    commit("teardown");
    this.reloadingPipelinePromise = CancelablePromise(
      new Promise(async (resolve) => {
        for (let i = 0; i < getters.statuses.length; i++) {
          const filters = {
            status_id: getters.statuses[i].id,
            ...getters.paramFilters,
          };

          await api.clientStore.initActiveState(rootState.market.market.id, filters)
            .then((response) => {
              commit("setup", response.data);
            });
        }

        commit("setPageLoading", false);
        resolve();
      })
    );
  },
};

const ActiveStateStore = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default ActiveStateStore;
